/**
 * Quick View Modal Module
 * Handles quick view modal for products
 */
class QuickView {
  constructor() {
    this.modal = document.getElementById('quick-view-modal');
    this.contentContainer = document.getElementById('quick-view-product-content');
    this.closeBtns = document.querySelectorAll('[data-quick-view-close]');
    
    if (!this.modal) return;
    
    this.init();
  }
  
  init() {
    this.setupTriggers();
    this.setupCloseButtons();
    this.setupEscapeKey();
  }
  
  setupTriggers() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-view]');
      if (trigger) {
        e.preventDefault();
        const productHandle = trigger.dataset.quickView;
        this.open(productHandle);
      }
    });
  }
  
  async open(productHandle) {
    if (!productHandle) return;
    
    this.modal.classList.add('active');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    this.showLoading();
    
    try {
      let rootUrl = typeof Shopify !== 'undefined' && Shopify.routes?.root_url 
        ? Shopify.routes.root_url 
        : '/';
      
      if (!rootUrl.endsWith('/')) {
        rootUrl += '/';
      }
      
      const productUrl = new URL(rootUrl + 'products/' + productHandle, window.location.origin);
      productUrl.searchParams.set('view', 'quick-view');
      
      const response = await fetch(productUrl.toString());
      if (!response.ok) throw new Error('Product not found');
      
      const html = await response.text();
      this.contentContainer.innerHTML = html;
      
      this.initializeProductForm();
      this.focusFirstElement();
      
    } catch (error) {
      console.error('Quick view error:', error);
      this.contentContainer.innerHTML = `
        <div class="labratgifts-quick-view-error">
          <p>Unable to load product. Please try again.</p>
        </div>
      `;
    }
  }
  
  close() {
    this.modal.classList.remove('active');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    setTimeout(() => {
      this.contentContainer.innerHTML = `
        <div class="labratgifts-quick-view-loading">
          <div class="labratgifts-spinner"></div>
          <p>Loading...</p>
        </div>
      `;
    }, 300);
  }
  
  showLoading() {
    this.contentContainer.innerHTML = `
      <div class="labratgifts-quick-view-loading">
        <div class="labratgifts-spinner"></div>
        <p>Loading...</p>
      </div>
    `;
  }
  
  setupCloseButtons() {
    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
  }
  
  setupEscapeKey() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.close();
      }
    });
  }
  
  initializeProductForm() {
    this.initializeQuantityButtons();
    
    const form = this.contentContainer.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const formData = new FormData(form);
      const addButton = form.querySelector('[type="submit"]');
      const originalText = addButton.textContent;
      
      addButton.disabled = true;
      addButton.textContent = 'Adding...';
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const addedItem = await response.json();
          addButton.textContent = 'Added!';
          
          // Get product title for notification
          const productTitle = this.contentContainer.querySelector('.labratgifts-product__title')?.textContent || 'Product';
          
          // Fetch current cart to get accurate count
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          // Update cart count
          const cartCountElements = document.querySelectorAll('[data-cart-count]');
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
          });
          
          // Refresh cart drawer content if it exists
          if (typeof window.labratgiftsTheme !== 'undefined' && window.labratgiftsTheme.refreshCartDrawer) {
            await window.labratgiftsTheme.refreshCartDrawer(cart);
          }
          
          // Generate unique request ID for this add-to-cart action
          const requestId = window.labratgiftsFBTCoordinator ? 
            window.labratgiftsFBTCoordinator.constructor.generateRequestId() : null;
          
          // Dispatch event with product details for FBT modal and toast (include request ID)
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: {
              product_id: addedItem.product_id,
              variant_id: addedItem.variant_id,
              quantity: addedItem.quantity,
              message: `${productTitle} added to cart!`,
              requestId: requestId
            }
          }));
          
          // Open cart drawer immediately - FBT modal will show on top if enabled
          setTimeout(() => {
            this.openCartDrawerWithRefresh();
          }, 300);
          
          // Don't close immediately - let FBT modal show
          setTimeout(() => {
            this.close();
          }, 1500);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        addButton.textContent = 'Error - Try again';
        setTimeout(() => {
          addButton.textContent = originalText;
          addButton.disabled = false;
        }, 2000);
      }
    });
    
    const gallery = this.contentContainer.querySelector('.labratgifts-product-gallery');
    if (gallery && typeof ProductGallery !== 'undefined') {
      new ProductGallery(gallery);
    }
    
    if (typeof QuickViewZoom !== 'undefined') {
      new QuickViewZoom();
    }
  }
  
  openCartDrawerWithRefresh() {
    const cartDrawer = document.getElementById('cartDrawer');
    if (!cartDrawer) return;
    
    cartDrawer.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // Show loader briefly then remove
    const drawerBody = cartDrawer.querySelector('.labratgifts-cart-drawer__body');
    if (drawerBody) {
      drawerBody.classList.add('labratgifts-cart-loading');
      
      // Remove loading state after 300ms - cart data is already updated via /cart.js
      setTimeout(() => {
        drawerBody.classList.remove('labratgifts-cart-loading');
      }, 300);
    }
  }

  initializeQuantityButtons() {
    const quantityInputs = this.contentContainer.querySelectorAll('[data-quantity-input]');
    const plusButtons = this.contentContainer.querySelectorAll('[data-quantity-plus]');
    const minusButtons = this.contentContainer.querySelectorAll('[data-quantity-minus]');
    
    quantityInputs.forEach(input => {
      input.addEventListener('change', function() {
        if (this.value < 1) {
          this.value = 1;
        }
      });
    });
    
    plusButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          input.value = parseInt(input.value) + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    
    minusButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input && parseInt(input.value) > 1) {
          input.value = parseInt(input.value) - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }
  
  focusFirstElement() {
    const firstFocusable = this.contentContainer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
  }
}

/**
 * Amazon-Style Zoom Overlay for Quick View
 * Full-screen overlay with gallery navigation and purchase controls
 */
class QuickViewZoom {
  constructor() {
    this.overlay = document.getElementById('quick-view-zoom-overlay');
    if (!this.overlay) return;
    
    this.viewport = this.overlay.querySelector('[data-zoom-viewport]');
    this.thumbnailsContainer = this.overlay.querySelector('[data-zoom-thumbnails]');
    this.currentIndex = 0;
    this.productData = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    this.init();
  }
  
  init() {
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    document.addEventListener('quickview:zoom-open', (e) => {
      this.currentIndex = e.detail?.currentIndex || 0;
      this.open();
    });
    
    const closeBtn = this.overlay.querySelector('[data-zoom-close]');
    const backdrop = this.overlay.querySelector('.labratgifts-zoom-overlay__backdrop');
    
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }
    
    if (backdrop) {
      backdrop.addEventListener('click', () => this.close());
    }
    
    const prevBtn = this.overlay.querySelector('[data-zoom-nav="prev"]');
    const nextBtn = this.overlay.querySelector('[data-zoom-nav="next"]');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.navigate(-1));
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.navigate(1));
    }
    
    this.setupQuantityControls();
    this.setupAddToCart();
    this.setupKeyboardNav();
    this.setupTouchNav();
  }
  
  open() {
    this.loadProductData();
    if (!this.productData) return;
    
    this.populateOverlay();
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    this.goToSlide(this.currentIndex);
  }
  
  close() {
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    
    const quickViewModal = document.getElementById('quick-view-modal');
    const isQuickViewActive = quickViewModal && quickViewModal.classList.contains('active');
    
    document.body.style.overflow = isQuickViewActive ? 'hidden' : '';
  }
  
  loadProductData() {
    const dataElement = document.getElementById('quick-view-zoom-data');
    if (!dataElement) return;
    
    try {
      this.productData = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('Failed to parse product data:', error);
    }
  }
  
  populateOverlay() {
    if (!this.productData) return;
    
    this.populateGallery();
    this.populateProductInfo();
  }
  
  populateGallery() {
    const media = this.productData.media || [];
    
    this.viewport.innerHTML = media.map((item, index) => `
      <div class="labratgifts-zoom-gallery__slide${index === 0 ? ' active' : ''}" data-index="${index}">
        <img src="${item.zoomUrl}" alt="${item.alt || ''}" loading="${index === 0 ? 'eager' : 'lazy'}">
      </div>
    `).join('');
    
    this.thumbnailsContainer.innerHTML = media.map((item, index) => `
      <button type="button" class="labratgifts-zoom-thumbnail${index === 0 ? ' active' : ''}" data-index="${index}">
        <img src="${item.thumbUrl}" alt="${item.alt || ''}">
      </button>
    `).join('');
    
    const thumbnails = this.thumbnailsContainer.querySelectorAll('.labratgifts-zoom-thumbnail');
    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goToSlide(index));
    });
  }
  
  populateProductInfo() {
    const data = this.productData;
    
    const vendorEl = this.overlay.querySelector('[data-zoom-vendor]');
    const titleEl = this.overlay.querySelector('[data-zoom-title]');
    const priceEl = this.overlay.querySelector('[data-zoom-price]');
    const comparePriceEl = this.overlay.querySelector('[data-zoom-compare-price]');
    const saleBadgeEl = this.overlay.querySelector('[data-zoom-sale-badge]');
    const savingsEl = this.overlay.querySelector('[data-zoom-savings]');
    const descriptionEl = this.overlay.querySelector('[data-zoom-description]');
    const cartBtn = this.overlay.querySelector('[data-zoom-add-to-cart]');
    
    if (vendorEl) vendorEl.textContent = data.vendor || '';
    if (titleEl) titleEl.textContent = data.title || '';
    
    if (priceEl) {
      priceEl.textContent = this.formatMoney(data.price);
    }
    
    const hasDiscount = data.compareAtPrice && data.compareAtPrice > data.price;
    
    if (comparePriceEl) {
      comparePriceEl.textContent = hasDiscount ? this.formatMoney(data.compareAtPrice) : '';
      comparePriceEl.style.display = hasDiscount ? 'inline' : 'none';
    }
    
    if (saleBadgeEl) {
      if (hasDiscount) {
        const percentage = Math.round(((data.compareAtPrice - data.price) / data.compareAtPrice) * 100);
        saleBadgeEl.textContent = `SAVE ${percentage}%`;
        saleBadgeEl.style.display = 'inline-block';
      } else {
        saleBadgeEl.style.display = 'none';
      }
    }
    
    if (savingsEl) {
      if (hasDiscount) {
        const savings = data.compareAtPrice - data.price;
        savingsEl.textContent = `You save ${this.formatMoney(savings)}`;
        savingsEl.style.display = 'block';
      } else {
        savingsEl.style.display = 'none';
      }
    }
    
    if (descriptionEl) {
      descriptionEl.textContent = data.description || '';
    }
    
    if (cartBtn) {
      const cartText = cartBtn.querySelector('[data-zoom-cart-text]');
      if (data.available) {
        cartBtn.disabled = false;
        if (cartText) cartText.textContent = 'Add to Cart';
      } else {
        cartBtn.disabled = true;
        if (cartText) cartText.textContent = 'Sold Out';
      }
    }
  }
  
  goToSlide(index) {
    const slides = this.viewport.querySelectorAll('.labratgifts-zoom-gallery__slide');
    const thumbnails = this.thumbnailsContainer.querySelectorAll('.labratgifts-zoom-thumbnail');
    
    if (index < 0 || index >= slides.length) return;
    
    slides.forEach(slide => slide.classList.remove('active'));
    thumbnails.forEach(thumb => thumb.classList.remove('active'));
    
    if (slides[index]) slides[index].classList.add('active');
    if (thumbnails[index]) {
      thumbnails[index].classList.add('active');
      thumbnails[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    
    this.currentIndex = index;
  }
  
  navigate(direction) {
    const slides = this.viewport.querySelectorAll('.labratgifts-zoom-gallery__slide');
    let newIndex = this.currentIndex + direction;
    
    if (newIndex < 0) newIndex = slides.length - 1;
    if (newIndex >= slides.length) newIndex = 0;
    
    this.goToSlide(newIndex);
  }
  
  setupQuantityControls() {
    const input = this.overlay.querySelector('[data-zoom-quantity-input]');
    const minusBtn = this.overlay.querySelector('[data-zoom-quantity-minus]');
    const plusBtn = this.overlay.querySelector('[data-zoom-quantity-plus]');
    
    if (input) {
      input.addEventListener('change', function() {
        if (this.value < 1) this.value = 1;
      });
    }
    
    if (minusBtn && input) {
      minusBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        if (currentValue > 1) {
          input.value = currentValue - 1;
        }
      });
    }
    
    if (plusBtn && input) {
      plusBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        input.value = currentValue + 1;
      });
    }
  }
  
  setupAddToCart() {
    const cartBtn = this.overlay.querySelector('[data-zoom-add-to-cart]');
    const cartText = this.overlay.querySelector('[data-zoom-cart-text]');
    const quantityInput = this.overlay.querySelector('[data-zoom-quantity-input]');
    
    if (!cartBtn) return;
    
    cartBtn.addEventListener('click', async () => {
      if (cartBtn.disabled || !this.productData) return;
      
      const originalText = cartText ? cartText.textContent : 'Add to Cart';
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
      
      cartBtn.disabled = true;
      if (cartText) cartText.textContent = 'Adding...';
      
      try {
        const formData = new FormData();
        formData.append('id', this.productData.productId);
        formData.append('quantity', quantity);
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const addedItem = await response.json();
          if (cartText) cartText.textContent = 'Added!';
          
          // Get product title for notification
          const productTitle = this.productData?.title || 'Product';
          
          // Fetch current cart to get accurate count
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          // Update cart count
          const cartCountElements = document.querySelectorAll('[data-cart-count]');
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
          });
          
          // Refresh cart drawer content if it exists
          if (typeof window.labratgiftsTheme !== 'undefined' && window.labratgiftsTheme.refreshCartDrawer) {
            await window.labratgiftsTheme.refreshCartDrawer(cart);
          }
          
          // Generate unique request ID for this add-to-cart action
          const requestId = window.labratgiftsFBTCoordinator ? 
            window.labratgiftsFBTCoordinator.constructor.generateRequestId() : null;
          
          // Dispatch event with product details for FBT modal and toast (include request ID)
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: {
              product_id: addedItem.product_id,
              variant_id: addedItem.variant_id,
              quantity: addedItem.quantity,
              message: `${productTitle} added to cart!`,
              requestId: requestId
            }
          }));
          
          // Open cart drawer immediately - FBT modal will show on top if enabled
          setTimeout(() => {
            this.openCartDrawerWithRefresh();
          }, 300);
          
          setTimeout(() => {
            this.close();
          }, 1500);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        if (cartText) cartText.textContent = 'Error - Try again';
        setTimeout(() => {
          if (cartText) cartText.textContent = originalText;
          cartBtn.disabled = false;
        }, 2000);
      }
    });
  }
  
  setupKeyboardNav() {
    document.addEventListener('keydown', (e) => {
      if (!this.overlay.classList.contains('is-open')) return;
      
      switch(e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.navigate(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.navigate(1);
          break;
      }
    });
  }
  
  setupTouchNav() {
    const viewport = this.viewport;
    if (!viewport) return;
    
    viewport.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    viewport.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
  }
  
  handleSwipe() {
    const threshold = 50;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.navigate(1);
      } else {
        this.navigate(-1);
      }
    }
  }
  
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }
    return `$${(cents / 100).toFixed(2)}`;
  }
}

// Auto-initialize Quick View when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuickView();
    new QuickViewZoom();
  });
} else {
  new QuickView();
  new QuickViewZoom();
}
